/**
 * S3 Upload Service
 * Handles file uploads to S3 with pre-signed URLs
 */

class S3Service {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
  }

  /**
   * Get pre-signed URL for file upload
   * @param {string} fileName - Name of the file
   * @param {string} fileType - MIME type of the file
   * @param {string} folder - Optional folder path in S3 (default: 'uploads')
   * @param {boolean} useLaravelStructure - Use Laravel path structure (no timestamp prefix)
   * @returns {Promise<{url: string, key: string}>}
   */
  async getPresignedUrl(fileName, fileType, folder = "uploads", useLaravelStructure = false) {
    try {
      const response = await fetch(`${this.baseUrl}/api/s3/presign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          fileName,
          fileType,
          folder,
          useLaravelStructure,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get upload URL");
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting presigned URL:", error);
      throw error;
    }
  }

  /**
   * Upload file to S3 using pre-signed URL
   * @param {File} file - File to upload
   * @param {string} presignedUrl - Pre-signed URL from getPresignedUrl
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<string>} - Returns the S3 key
   */
  async uploadFile(file, presignedUrl, onProgress = null) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      // Handle response
      xhr.addEventListener("load", () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      // Handle errors
      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload aborted"));
      });

      // Send request
      xhr.open("PUT", presignedUrl);
      // Explicitly set Content-Type to match what was signed in the presigned URL.
      // The presigned URL is generated with file.type as ContentType, so we must
      // send the same value. Without this, some mobile browsers omit the header
      // or send a different type, causing S3 SignatureDoesNotMatch errors.
      // (Consistent with the standalone uploadFileToS3WithProgress utility below.)
      if (file.type) {
        xhr.setRequestHeader("Content-Type", file.type);
      }
      xhr.send(file);
    });
  }

  /**
   * Complete file upload process
   * @param {File} file - File to upload
   * @param {string} folder - Optional folder path
   * @param {Function} onProgress - Optional progress callback
   * @param {boolean} useLaravelStructure - Use Laravel path structure (no timestamp prefix)
   * @returns {Promise<string>} - Returns the S3 key
   */
  async uploadFileToS3(file, folder = "uploads", onProgress = null, useLaravelStructure = false) {
    try {
      // Validate file
      if (!file) {
        throw new Error("No file provided");
      }
      console.log("[S3 UPLOAD] file.type sent to backend:", file.type);
      // Get presigned URL
      const { url, key } = await this.getPresignedUrl(
        file.name,
        file.type,
        folder,
        useLaravelStructure
      );

      // Upload file
      await this.uploadFile(file, url, onProgress);

      return key;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw error;
    }
  }

  /**
   * Upload image with validation
   * @param {File} file - Image file to upload
   * @param {Object} options - Upload options
   * @param {number} options.maxSize - Maximum file size in bytes
   * @param {string[]} options.allowedTypes - Allowed MIME types
   * @param {string} options.folder - S3 folder path
   * @param {Function} options.onProgress - Progress callback
   * @returns {Promise<string>} - Returns the S3 key
   */
  async uploadImage(file, options = {}) {
    const {
      maxSize = 10 * 1024 * 1024, // 10MB default
      allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"],
      folder = "images",
      onProgress = null,
    } = options;

    // Validate file size
    if (file.size > maxSize) {
      throw new Error(
        `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`
      );
    }

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type must be one of: ${allowedTypes.join(", ")}`);
    }

    // Validate it's actually an image
    if (!file.type.startsWith("image/")) {
      throw new Error("File must be an image");
    }
    console.log("[S3 UPLOAD] file.type for uploadImage:", file.type);
    return this.uploadFileToS3(file, folder, onProgress);
  }

  /**
   * Upload document with validation
   * @param {File} file - Document file to upload
   * @param {Object} options - Upload options
   * @param {number} options.maxSize - Maximum file size in bytes
   * @param {string[]} options.allowedTypes - Allowed MIME types
   * @param {string} options.folder - S3 folder path
   * @param {Function} options.onProgress - Progress callback
   * @param {boolean} options.useLaravelStructure - Use Laravel path structure (no timestamp prefix)
   * @returns {Promise<string>} - Returns the S3 key
   */
  async uploadDocument(file, options = {}) {
    const {
      maxSize = 50 * 1024 * 1024, // 50MB default
      allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ],
      folder = "documents",
      onProgress = null,
      useLaravelStructure = false,
    } = options;

    // Validate file size
    if (file.size > maxSize) {
      throw new Error(
        `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`
      );
    }

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type must be one of: ${allowedTypes.join(", ")}`);
    }

    return this.uploadFileToS3(file, folder, onProgress, useLaravelStructure);
  }

  /**
   * Get S3 file URL
   * @param {string} key - S3 key
   * @returns {string} - Full S3 URL
   */
  getFileUrl(key) {
    if (!key) return null;

    // Debug: Log the type of key being passed
    console.log("🔍 [DEBUG] S3Service.getFileUrl called with:", {
      key,
      type: typeof key,
      isString: typeof key === "string",
      isArray: Array.isArray(key),
      isObject: typeof key === "object" && key !== null,
    });

    // Ensure key is a string
    const keyStr = String(key);

    // If it's already a full URL, return as is
    if (keyStr.startsWith("http")) {
      return keyStr;
    }

    // Use CDN if available, otherwise fall back to S3
    const cdnName = process.env.REACT_APP_S3_CDN_NAME;
    if (cdnName) {
      // Remove trailing slash from CDN name if it exists, then add key
      const cleanCdnName = cdnName.endsWith("/")
        ? cdnName.slice(0, -1)
        : cdnName;
      const url = `https://${cleanCdnName}/${keyStr}`;
      console.log("s3Service.getFileUrl: CDN URL construction:", {
        originalCdnName: cdnName,
        cleanCdnName,
        key: keyStr,
        finalUrl: url,
      });
      return url;
    }

    // Fallback to S3 URL
    const bucket = process.env.REACT_APP_S3_BUCKET || "your-bucket-name";
    const region = process.env.REACT_APP_S3_REGION || "us-east-1";
    const url = `https://${bucket}.s3.${region}.amazonaws.com/${keyStr}`;
    console.log("s3Service.getFileUrl: S3 fallback URL construction:", {
      bucket,
      region,
      key: keyStr,
      finalUrl: url,
    });
    return url;
  }

  /**
   * Get file URL from backend (with CDN support)
   * @param {string} key - S3 key
   * @returns {Promise<string>} - Full file URL
   */
  async getFileUrlFromBackend(key) {
    if (!key) return null;

    // Ensure key is a string
    const keyStr = String(key);

    // If it's already a full URL, return as is
    if (keyStr.startsWith("http")) {
      return keyStr;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/s3/url/${encodeURIComponent(keyStr)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get file URL");
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error("Error getting file URL from backend:", error);
      // Fallback to local URL construction
      return this.getFileUrl(keyStr);
    }
  }

  /**
   * Delete file from S3 (requires backend endpoint)
   * @param {string} key - S3 key to delete
   * @returns {Promise<boolean>}
   */
  async deleteFile(key) {
    try {
      const response = await fetch(`${this.baseUrl}/api/s3/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ key }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete file");
      }

      return true;
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  }

  /**
   * Generic upload method (alias for uploadFileToS3)
   * @param {File} file - File to upload
   * @param {string} folder - S3 folder path
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<{key: string}>} - Returns object with S3 key
   */
  async upload(file, folder = "uploads", onProgress = null) {
    try {
      const key = await this.uploadFileToS3(file, folder, onProgress);
      return { key };
    } catch (error) {
      console.error("Error in upload:", error);
      throw error;
    }
  }

  /**
   * Download file from S3
   * @param {string} key - S3 key
   * @param {string} fileName - Name for the downloaded file
   * @returns {Promise<void>}
   */
  async downloadFile(key, fileName) {
    try {
      const downloadUrl = await this.getFileUrlFromBackend(key);

      if (!downloadUrl) {
        throw new Error("Unable to generate download URL");
      }

      // Try blob approach first (works for most files)
      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();

        // Create a blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;

        // Add to DOM, click, and cleanup
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
      } catch (blobError) {
        console.warn(
          "Blob download failed, trying direct download:",
          blobError
        );

        // Fallback: direct download approach
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        link.style.display = "none";

        // Add to DOM, click, and cleanup
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      throw error;
    }
  }
}

// Create singleton instance
const s3Service = new S3Service();

export default s3Service;

// Utility: Upload file to S3 with progress tracking
export function uploadFileToS3WithProgress(file, presignedUrl, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status === 200) resolve();
      else reject(new Error("Upload failed: " + xhr.status));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });
}

// Utility: Upload file to S3 with fetch (no progress tracking)
export async function uploadFileToS3WithFetch(file, presignedUrl) {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type, // Only set if you set ContentType in presign!
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error("Upload failed: " + (await response.text()));
  }
  return true;
}

// Example usage in a React component (for documentation):
//
// import { uploadFileToS3WithProgress } from './services/s3Service';
//
// async function getPresignedUrl(file) {
//   const response = await fetch('/api/s3/presign', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({
//       fileName: file.name,
//       fileType: file.type,
//       folder: 'media'
//     }),
//   });
//   if (!response.ok) throw new Error('Failed to get presigned URL');
//   return await response.json(); // { url, key }
// }
//
// // In your React component:
// const [progress, setProgress] = useState(0);
// const handleFileChange = async (e) => {
//   const file = e.target.files[0];
//   if (!file) return;
//   setProgress(0);
//   const { url, key } = await getPresignedUrl(file);
//   await uploadFileToS3WithProgress(file, url, setProgress);
//   // ... handle success ...
// };
